// src/pages/Login.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { isActivatedComplete, useUser } from '../contexts/UserContext';
import Spinner from '../components/Spinner';
import { api } from '../utilities/api';
import { validatePassword } from '../utilities/utilities';
import { createPasskeyAssertion, isPasskeySupported } from '../utilities/webauthn';
import logo from '../assets/RWM Logo-270x270.png';

const REMEMBERED_USER_NAME_KEY = 'redimdwm_remembered_user_name';
const PASSKEYS_ENABLED = import.meta.env.VITE_ENABLE_PASSKEYS !== 'false';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const userContext = useUser();
  
  // State for different views
  const [view, setView] = useState('login'); // 'login', 'forgot', 'reset'
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetCode, setResetCode] = useState(null);
  const [loginName, setLoginName] = useState(() => localStorage.getItem(REMEMBERED_USER_NAME_KEY) || '');
  const [rememberLoginName, setRememberLoginName] = useState(() => Boolean(localStorage.getItem(REMEMBERED_USER_NAME_KEY)));
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showPasskeyHelp, setShowPasskeyHelp] = useState(false);
  const [passkeyMessage, setPasskeyMessage] = useState('');

  const getPasskeyErrorMessage = (error) => {
    const message = String(error?.message || '').trim();
    const normalized = message.toLowerCase();

    if (!message || error?.name === 'NotAllowedError' || normalized.includes('cancel')) {
      return 'No passkey was found, or the device prompt was cancelled. Sign in with your password, then set up a passkey after login.';
    }

    if (normalized.includes('not supported') || normalized.includes('secure context')) {
      return 'This browser or device cannot use passkeys. Sign in with your password instead.';
    }

    if (normalized.includes('not available') || normalized.includes('not found') || normalized.includes('no passkey')) {
      return 'No passkey is set up for this login name. Sign in with your password, then set up a passkey after login.';
    }

    if (normalized.includes('unauthorized') || normalized.includes('invalid passkey')) {
      return 'That passkey did not sign you in. Sign in with your password, then check your passkey setup in Profile.';
    }

    return `${message} Sign in with your password if passkey sign-in does not work.`;
  };

  // Check for password reset deep link on component mount
  useEffect(() => {
    userContext.setError('');
    if (location.state?.successMessage) {
      userContext.setSuccess(location.state.successMessage);
    } else {
      userContext.setSuccess('');
    }

    const fragmentParams = new URLSearchParams(location.hash.replace(/^#/, ''));
    const step = fragmentParams.get('step');
    const token = fragmentParams.get('token');
    const code = fragmentParams.get('code');
    
    if (step === 'reset' && token && code) {
      setView('reset');
      setResetToken(token);
      setResetCode(code);
      window.history.replaceState(null, document.title, location.pathname);
    } else if (step === 'reset') {
      setView('login');
      userContext.setError('This password reset link is incomplete or expired. Request a new reset link to continue.');
      window.history.replaceState(null, document.title, location.pathname);
    }
  }, [location]);

  const finishLogin = (loginResult, fallbackLoginName) => {
    const activated = loginResult.profile?.activated ?? loginResult.user?.activated;

    if (isActivatedComplete(activated)) {
      const from = location.state?.from;
      const destination = from?.pathname
        ? `${from.pathname || ''}${from.search || ''}${from.hash || ''}`
        : '/dashboard';
      navigate(destination, { replace: true });
      return;
    }

    const registrationDraft = {
      ...(loginResult.profile || {}),
      ...(loginResult.user || {}),
      userid: loginResult.profile?.userid || loginResult.user?.userid || loginResult.userid,
      user_name: loginResult.profile?.user_name || loginResult.user?.user_name || loginResult.user_name || fallbackLoginName,
      email: loginResult.profile?.email || loginResult.user?.email || fallbackLoginName,
      registration_step: loginResult.profile?.registration_step_normalized || loginResult.profile?.registration_step || loginResult.user?.registration_step_normalized || loginResult.user?.registration_step || 'register',
      activated: '0',
    };
    localStorage.setItem('redimdwm_registration_data', JSON.stringify(registrationDraft));
    userContext.setError("Please complete registration to access your account.");
    navigate('/register');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    userContext.setError('');
    userContext.setSuccess('');
    setPasskeyMessage('');

    const submittedLoginName = e.target.email.value.trim();
    const shouldRememberLoginName = Boolean(e.target.elements['remember-me']?.checked);
    const tzoffset = new Date().getTimezoneOffset();
    const data = {
      user_name: submittedLoginName,
      identifier: submittedLoginName,
      email: submittedLoginName,
      password: e.target.password.value,
      tzoffset: tzoffset,
      remember_me: shouldRememberLoginName,
    };

    if (!shouldRememberLoginName) {
      localStorage.removeItem(REMEMBERED_USER_NAME_KEY);
    }

    try {
      const loginResult = await userContext.login(data);
      if (!loginResult?.success) return;

      if (shouldRememberLoginName) {
        localStorage.setItem(REMEMBERED_USER_NAME_KEY, submittedLoginName);
      }

      finishLogin(loginResult, submittedLoginName);
    } catch {
      userContext.setError("An unexpected network error occurred. Please try again.");
    }
  };

  const handlePasskeyLogin = async () => {
    userContext.setError('');
    userContext.setSuccess('');
    setPasskeyMessage('');

    const submittedLoginName = loginName.trim();

    if (!submittedLoginName) {
      setPasskeyMessage('Enter your email or mobile login first, then choose Sign in with passkey.');
      return;
    }

    if (!isPasskeySupported()) {
      setPasskeyMessage('This browser or device cannot use passkeys. Sign in with your password instead.');
      return;
    }

    setPasskeyLoading(true);

    try {
      const optionsResult = await api.getPasskeyLoginOptions({
        user_name: submittedLoginName,
        identifier: submittedLoginName,
        origin: window.location.origin,
      });

      if (optionsResult.error) {
        throw new Error(optionsResult.error);
      }

      const assertion = await createPasskeyAssertion(optionsResult);
      const loginResult = await userContext.passkeyLogin({
        challengeId: optionsResult.challengeId,
        challenge: optionsResult.publicKey?.challenge || optionsResult.challenge,
        user_name: submittedLoginName,
        identifier: submittedLoginName,
        assertion,
        origin: window.location.origin,
      });

      if (!loginResult?.success) return;

      if (rememberLoginName && submittedLoginName) {
        localStorage.setItem(REMEMBERED_USER_NAME_KEY, submittedLoginName);
      }

      finishLogin(loginResult, submittedLoginName);
    } catch (err) {
      setPasskeyMessage(getPasskeyErrorMessage(err));
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const loginName = e.target.user_name.value.trim();
      const result = await api.forgotPassword({
        user_name: loginName,
        identifier: loginName,
        email: loginName,
        origin: window.location.origin,
      });
      
      if (result.success) {
        userContext.setError(''); // Clear any existing errors
        userContext.setSuccess('Password reset instructions have been sent to your verified recovery channel. Please follow the link to reset your password.');
        setView('login');
      } else {
        userContext.setError(result.error || 'Failed to send password reset instructions. Please try again.');
      }
    } catch {
      userContext.setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const password = e.target.password.value;
      const confirmPassword = e.target.confirmPassword.value;
      
      if (password !== confirmPassword) {
        userContext.setError('Passwords do not match.');
        setLoading(false);
        return;
      }
      
      const passwordError = validatePassword(password);
      if (passwordError) {
        userContext.setError(passwordError);
        setLoading(false);
        return;
      }
      
      const result = await api.resetPassword({
        token: resetToken,
        code: resetCode,
        password: password
      });
      
      if (result.success) {
        userContext.setError(''); // Clear any existing errors
        const successMessage = 'Password reset successful! You can now log in with your new password.';
        userContext.setSuccess(successMessage);
        setView('login');
        setResetToken(null);
        setResetCode(null);
        // Clear URL parameters
        navigate('/login', { replace: true, state: { successMessage } });
      } else {
        userContext.setError(result.error || 'Failed to reset password. Please try again or request a new reset link.');
      }
    } catch {
      userContext.setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = () => (
    <>
      {/* Error/Success Display */}
      {userContext.error && (
        <div className="mb-0 p-3 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
          <p className="font-bold">
            {userContext.error.startsWith('This password reset link')
              ? 'Password reset link issue'
              : 'Login Failed'}
          </p>
          <p className="text-sm">{userContext.error}</p>
        </div>
      )}
      
      {userContext.success && (
        <div className="mb-0 p-3 bg-green-100 border border-green-400 text-green-700 rounded" role="alert">
          <p className="text-sm">{userContext.success}</p>
        </div>
      )}

      {/* Loading Spinner */}
      {userContext.loadingSession && <Spinner />}

      {/* Login Form */}
      {!userContext.loadingSession && (
        <form className="mt-0 space-y-4" onSubmit={handleLoginSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <label htmlFor="email-address" className="sr-only">Login name</label>
              <input
                id="email-address"
                name="email"
                type="text"
                autoComplete="username"
                required
                value={loginName}
                onChange={(event) => setLoginName(event.target.value)}
                className="appearance-none rounded relative block w-full px-3 py-2 border border-neutral placeholder-neutral-dark text-neutral-darkest focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Email or mobile login"
              />
            </div>

            <div className="flex items-center">
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded relative block w-full px-3 py-2 border border-neutral placeholder-neutral-dark text-neutral-darkest focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div className="flex items-center">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberLoginName}
                onChange={(event) => setRememberLoginName(event.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary border-neutral rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-neutral-darkest"> Remember login name </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-button hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150 ease-in-out"
              disabled={userContext?.loadingSession}
            >
              Sign in
            </button>
          </div>

          {PASSKEYS_ENABLED && (
          <div className="space-y-2">
            <div>
              <button
                type="button"
                className="group relative flex w-full justify-center rounded-md border border-primary bg-white px-4 py-2 text-sm font-medium text-primary transition duration-150 ease-in-out hover:bg-primary-lightest focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
                disabled={userContext?.loadingSession || passkeyLoading}
                onClick={handlePasskeyLogin}
              >
                {passkeyLoading ? 'Checking passkey...' : 'Sign in with passkey'}
              </button>
            </div>
            <div className="text-center text-sm">
              <button
                type="button"
                onClick={() => setShowPasskeyHelp(true)}
                className="inline-flex items-center justify-center gap-1 font-medium text-primary hover:text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-haspopup="dialog"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-primary text-xs font-bold" aria-hidden="true">
                  ?
                </span>
                What is a passkey?
              </button>
            </div>
            {passkeyMessage && (
              <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-900" role="status">
                {passkeyMessage}
              </div>
            )}
          </div>
          )}

          <div className="flex items-center justify-center">
            <div className="text-sm ">
              <button
                type="button"
                onClick={() => {
                  setView('forgot');
                  userContext.setError('');
                  userContext.setSuccess('');
                }}
                className="font-medium text-primary hover:text-primary-dark"
              >
                Forgot your password?
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Link to Register */}
      {!userContext.loadingSession && (
        <div className="mt-6 text-center text-sm">
          <p className="text-neutral-dark">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-primary hover:text-primary-dark">
              Register here
            </Link>
          </p>
        </div>
      )}
    </>
  );

  const renderForgotPasswordForm = () => (
    <>
      {userContext.error && (
        <div className="mb-0 p-3 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
          <p className="font-bold">Error</p>
          <p className="text-sm">{userContext.error}</p>
        </div>
      )}

      <form className="mt-0 space-y-4" onSubmit={handleForgotPasswordSubmit}>
        <div className="text-center mb-4">
          <p className="text-sm text-neutral-dark">
            Enter your login name and we'll send a reset link to your verified recovery channel.
          </p>
        </div>

        <div className="rounded-md shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <label htmlFor="forgot-user-name" className="block text-sm font-medium text-neutral-dark">Username (email or mobile phone)</label>
            <input
              id="forgot-user-name"
              name="user_name"
              type="text"
              autoComplete="username"
              required
              className="appearance-none rounded relative block w-full px-3 py-2 border border-neutral placeholder-neutral-dark text-neutral-darkest focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
              placeholder="Email address or mobile phone"
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-button hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150 ease-in-out disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </div>

        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={() => {
              setView('login');
              userContext.setError('');
            }}
            className="text-sm font-medium text-primary hover:text-primary-dark"
          >
            Back to Sign In
          </button>
        </div>
      </form>
    </>
  );

  const renderResetPasswordForm = () => (
    <>
      {userContext.error && (
        <div className="mb-0 p-3 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
          <p className="font-bold">Error</p>
          <p className="text-sm">{userContext.error}</p>
        </div>
      )}

      <form className="mt-0 space-y-4" onSubmit={handleResetPasswordSubmit}>
        <div className="text-center mb-4">
          <p className="text-sm text-neutral-dark">
            Enter your new password below.
          </p>
        </div>

        <div className="rounded-md shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <label htmlFor="new-password" className="sr-only">New Password</label>
            <input
              id="new-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength="8"
              className="appearance-none rounded relative block w-full px-3 py-2 border border-neutral placeholder-neutral-dark text-neutral-darkest focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
              placeholder="New password"
            />
          </div>

          <div className="flex items-center">
            <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength="8"
              className="appearance-none rounded relative block w-full px-3 py-2 border border-neutral placeholder-neutral-dark text-neutral-darkest focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
              placeholder="Confirm new password"
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-button hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150 ease-in-out disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
      </form>
    </>
  );

  // Determine title based on view
  const getTitle = () => {
    switch (view) {
      case 'forgot':
        return 'Reset your password';
      case 'reset':
        return 'Set new password';
      default:
        return 'Sign in to your account';
    }
  };

  // --- JSX ---
  return (
    <div className="flex min-h-[calc(100svh-4rem)] items-center justify-center bg-highlight px-4 py-3 sm:min-h-[calc(100vh-4rem)] sm:py-6">
      <div className="flex w-full max-w-96 flex-col rounded-lg bg-white p-4 shadow-2xl sm:p-5">
        <div>
          <img className="mx-auto h-24 w-auto sm:h-32" src={logo} alt="Logo" />
          <h2 className="mt-3 mb-4 text-center text-2xl font-extrabold text-primary sm:text-3xl">
            {getTitle()}
          </h2>
        </div>

        {view === 'login' && renderLoginForm()}
        {view === 'forgot' && renderForgotPasswordForm()}
        {view === 'reset' && renderResetPasswordForm()}
      </div>

      {showPasskeyHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div
            className="w-full max-w-sm rounded-md bg-white p-5 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="passkey-help-title"
          >
            <h3 id="passkey-help-title" className="text-lg font-semibold text-primary">
              What is a passkey?
            </h3>
            <p className="mt-3 text-sm text-neutral-darkest">
              A passkey lets you sign in with your device lock, like Touch ID, Face ID, Windows Hello, or your phone screen lock. It replaces typing your password on this device.
            </p>
            <p className="mt-2 text-sm text-neutral-darkest">
              To use one, choose Sign in with passkey and follow your device prompt.
            </p>
            <button
              type="button"
              onClick={() => setShowPasskeyHelp(false)}
              className="mt-5 w-full rounded-md bg-button px-4 py-2 text-sm font-medium text-white hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
